import { type FC, useCallback, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import type { GenericOption } from 'pages/SmartConnectors/types';
import { useSelectTypeaheadWithScroll } from '__components/Inputs/Select/hooks';
import MultiSelect from '__components/Inputs/MultiSelect';
import { SmartConnectorContext } from '../../context';
import ApplySelectButton from '__components/Inputs/Select/ApplyButton';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';
import { Entities } from 'components/Inputs/Select/hooks';
import type { LoggedActivityEntry } from '@kizen/api/activities';
import { formatTimestamp } from 'components/Inputs/DateTimeInput/helpers';

export const CreateActivityModal: FC<{
  onConfirm: (loggedActivities: LoggedActivityEntry[]) => void;
  onHide: () => void;
}> = ({ onConfirm, onHide }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<
    (GenericOption & { data: LoggedActivityEntry })[]
  >([]);

  const { smartConnector } = useContext(SmartConnectorContext);

  const handleSubmit = useCallback(() => {
    onConfirm(selected.map((o) => o.data));
  }, [onConfirm, selected]);

  const selectRef = useRef(null);

  const [typeaheadProps, { loading }] = useSelectTypeaheadWithScroll({
    enabled: !!smartConnector.activity_object,
    entity: Entities.LoggedActivities,
    selectRef,
    chosenValueIds: selected.map((o) => o.value),
    relatedObjectId: smartConnector.activity_object?.id,
    objectToOption: (data: LoggedActivityEntry) => ({
      label: `${formatTimestamp(new Date(data.logged_at))} - ${data.logged_by?.display_name}`,
      value: data.id,
      data,
    }),
    keepPreviousData: false,
  });

  return (
    <BasicModal
      size="medium"
      show
      onConfirm={handleSubmit}
      onHide={onHide}
      heading={t('Sample Activity Payload')}
      disabled={!selected.length}
    >
      <MultiSelect
        ref={selectRef}
        value={selected}
        label={t('Sample Logged Activities')}
        onChange={setSelected}
        placeholder={t('Choose Logged Activities')}
        {...typeaheadProps}
        isLoading={loading}
        portal
        menuRightButton={<ApplySelectButton />}
        menuLeftButton={<ClearSelectButton />}
      />
    </BasicModal>
  );
};
